import { Formik } from "formik";
import _ from "lodash";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CustomTabs from "../../../components/CustomTabs";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import NewTableComponent from "../../../components/NewTableComponent";
import Select from "../../../components/Select";
import UserAvatar from "../../../components/UserAvatar";
import {
    fetchPartCategoriesDropdown, fetchPartsMakesDropdown,
    fetchPartsMaterialDropdown, fetchPartsModelDropdown,
    fetchPartsUnitsDropdown
} from "../../../store/Slices/dropdownSlice";
import { getPartDetail, getPartPaginatedForPO, getPartUtilization, partUpdate } from "../../../store/Slices/PartsSlice";
import '../../../styles/Parts.css';
import { formats, modules, partInitialValues } from "../../../utils/helper";
import SpinnerLoader from "../../../utils/SpinnerLoader";
import {
    activeDetailsIcon, activeHistoryIcon,
    activeViolationsIcon,
    detailsIcon, historyIcon,
    violationsIcon
} from "../../../utils/svgs";
import { partFullSchema } from '../../../utils/Schema';
import { errorNotify, successNotify } from "../../../utils/toast";
import PermissionWrapper from '../../../components/PermissionWrapper';
import { AppMainModules, InventorySubModules, PartsPermission } from '../../../utils/app-constant';
import ReactQuill from "react-quill-new";
import PartHistory from "./PartHistory";

const PartDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const {
        getPartDetailData,
        getPartDetailLoading,
        getPartPaginatedForPOData,
        getPartPaginatedForPOloading,
        getPartUtilizationData,
        getPartUtilizationLoading,
        partUpdateLoading
    } = useSelector((state) => state.parts);
    const { userDropdownData = [] } = useSelector((state) => state.users || {});
    const { loading: dropdownLoading, partCategoriesDropdownData, partsMaterialDropdownData,
        partsMakesDropdownData, partsModelDropdownData, partsUnitsDropdownData } = useSelector((state) => state.dropdowns);

    const [activeTab, setActiveTab] = useState("detail");
    const [initialValues, setInitialValues] = useState(partInitialValues);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [initialUploadedFileNames, setInitialUploadedFileNames] = useState([]);
    const [procurementCurrentPage, setProcurementCurrentPage] = useState(1);
    const [procurementLimit, setProcurementLimit] = useState(10);
    const [utilizationCurrentPage, setUtilizationCurrentPage] = useState(1);
    const [utilizationLimit, setUtilizationLimit] = useState(10);

    useEffect(() => {
        dispatch(getPartDetail(id));
        dispatch(fetchPartCategoriesDropdown());
        dispatch(fetchPartsMaterialDropdown());
        dispatch(fetchPartsModelDropdown());
        dispatch(fetchPartsMakesDropdown());
        dispatch(fetchPartsUnitsDropdown());
    }, [id, dispatch]);

    useEffect(() => {
        if (getPartDetailData) {
            const d = getPartDetailData;
            const s = d.specifications || {};
            const rawFiles = d.images || d.files || d.attachments || [];
            const mappedFiles = rawFiles
                .map((file) => {
                    if (typeof file === "string") {
                        return { fileName: file, originalName: file };
                    }
                    const fileName =
                        file?.fileName ||
                        file?.file_name ||
                        file?.file_path ||
                        file?.url ||
                        file?.path ||
                        file?.file ||
                        file?.image ||
                        "";

                    if (!fileName) return null;

                    return {
                        ...file,
                        id: file?.id || file?.file_id || file?.gallery_id,
                        fileName,
                        originalName: file?.originalName || file?.original_name || fileName,
                        previewUrl: null,
                    };
                })
                .filter(Boolean);

            setInitialValues({
                part_name: d.name ?? "",
                company_name: d.company_name ?? "",
                part_number: d.part_number ?? "",
                sku: d.sku ?? "",
                category: d.maintenance_category_id ?? "",
                description: d.description ?? "",
                length: s.length ?? "",
                width: s.width ?? "",
                height: s.height ?? "",
                weight: s.weight ?? "",
                material: s.material ?? "",
                model: s.model ?? "",
                year: s.year ?? "",
                make: s.make ?? "",
                engineCode: s.engine_code ?? "",
                crossReferenceSku: s.cross_reference_sku ?? "",
                maxPressure: s.max_pressure ?? "",
                maxVoltage: s.max_voltage ?? "",
                maxTemperature: s.max_temperature ?? "",
                unitOfMeasurement: d.unit_id ?? "",
                safetyStockThreshold: d.stock_threshold ?? "",
                availableStock: d.stock_count ?? "",
                price: d.price ?? "",
                vendor: d.vendor_id ?? "",
                notes: d.note ?? ""
            });
            setUploadedFiles(mappedFiles);
            setInitialUploadedFileNames(mappedFiles.map((file) => file.fileName).filter(Boolean));
        }
    }, [getPartDetailData]);

    useEffect(() => {
        if (activeTab !== "procurement" || !id) return;
        dispatch(getPartPaginatedForPO({
            page: procurementCurrentPage,
            limit: procurementLimit,
            part_id: id,
        }));
    }, [activeTab, id, dispatch, procurementCurrentPage, procurementLimit]);

    useEffect(() => {
        if (activeTab !== "utilization" || !id) return;
        dispatch(getPartUtilization({
            page: utilizationCurrentPage,
            limit: utilizationLimit,
            part_id: id,
        }));
    }, [activeTab, id, dispatch, utilizationCurrentPage, utilizationLimit]);

    const formatDate = (date) => {
        if (!date) return "-";
        const parsed = new Date(date);
        if (Number.isNaN(parsed.getTime())) return "-";
        return parsed.toISOString().slice(0, 10);
    };

    const formatAmount = (value) => {
        const numericValue = Number(value || 0);
        if (!Number.isFinite(numericValue)) return "0";
        return numericValue.toLocaleString();
    };

    const procurementRows = getPartPaginatedForPOData?.results || [];
    const procurementMeta = getPartPaginatedForPOData?.meta || {};
    const utilizationRows = getPartUtilizationData?.results || [];
    const utilizationMeta = getPartUtilizationData?.meta || {};

    const procurementColumns = [
        {
            name: "Delivered Date",
            selector: (row) => formatDate(row?.created_at),
            sortable: false,
            minWidth: "160px",
        },
        {
            name: "PO ID",
            selector: (row) => row?.po_id?.parts_purchase_order_id || `PO-${row?.parts_purchase_order_id ?? "-"}`,
            cell: (row) => (
                <span
                    className="hyperlink"
                    onClick={() => {
                        const poDetailId = row?.po_id?.id || row?.parts_purchase_order_id;
                        if (poDetailId) navigate(`/po/details/${poDetailId}`);
                    }}
                    style={{ cursor: "pointer" }}
                >
                    {row?.po_id?.parts_purchase_order_id || `PO-${row?.parts_purchase_order_id ?? "-"}`}
                </span>
            ),
            sortable: false,
            minWidth: "160px",
        },
        {
            name: "Vendor Name",
            selector: (row) => row?.vendor?.name || row?.vendor_name || (row?.vendor_id ? `Vendor-${row.vendor_id}` : "-"),
            sortable: false,
            minWidth: "170px",
            wrap: true,
        },
        {
            name: "Unit Cost (PKR)",
            selector: (row) => formatAmount(row?.unit_price),
            sortable: false,
            minWidth: "160px",
        },
        {
            name: "Quantity Received",
            selector: (row) => Number(row?.actual_quantity || 0),
            sortable: false,
            minWidth: "160px",
        },
        {
            name: "Good Quantity",
            selector: (row) => Number(row?.good_quantity || 0),
            sortable: false,
            minWidth: "130px",
        },
        {
            name: "Damaged Quantity",
            selector: (row) => Number(row?.damaged_quantity || 0),
            sortable: false,
            minWidth: "150px",
        },
        {
            name: "Incorrect Quantity",
            selector: (row) => Number(row?.incorrect_quantity || 0),
            sortable: false,
            minWidth: "160px",
        },
        {
            name: "Landed Cost (PKR)",
            selector: (row) => formatAmount(
                row?.line_total ?? ((Number(row?.unit_price || 0) * Number(row?.ordered_quantity || 0)))
            ),
            sortable: false,
            minWidth: "170px",
        },
    ];

    const getRequesterFromRow = (row) => {
        const apiUser =
            row?.created_by_user ||
            row?.created_by_user_details ||
            row?.service_line_item?.created_by_user ||
            row?.service_line_item?.user ||
            null;

        const createdById = Number(row?.service_line_item?.created_by ?? row?.created_by ?? 0);
        const dropdownUser = userDropdownData.find((user) => Number(user?.value) === createdById);

        return {
            username:
                apiUser?.username ||
                apiUser?.name ||
                dropdownUser?.label ||
                (createdById ? `User-${createdById}` : "-"),
            image: apiUser?.image || dropdownUser?.image || "",
        };
    };

    const getUtilizationWorkOrderLabel = (row) => {
        const nestedLabel = row?.service_line_item?.work_order?.work_order_id;
        if (nestedLabel) return nestedLabel;
        const workOrderId = Number(row?.service_line_item?.work_order_id || 0);
        return workOrderId > 0 ? `WO-${workOrderId}` : "-";
    };

    const getUtilizationWorkOrderRouteId = (row) => {
        const nestedId = row?.service_line_item?.work_order?.id;
        if (nestedId) return nestedId;
        const workOrderId = Number(row?.service_line_item?.work_order_id || 0);
        return workOrderId > 0 ? workOrderId : null;
    };

    const getAssetLabel = (row) => (
        row?.service_line_item?.vehicle?.asset_id ||
        row?.service_line_item?.vehicle?.asset_no ||
        row?.service_line_item?.vehicle?.registration_number ||
        row?.service_line_item?.vehicle?.registration_no ||
        row?.service_line_item?.vehicle?.vehicle_number ||
        row?.service_line_item?.service?.service_id ||
        (row?.service_line_item?.vehicle_id ? `VH-${row?.service_line_item?.vehicle_id}` : "-")
    );

    const getUtilizationLandedCost = (row) => (
        row?.service_line_item?.cost_parts ??
        row?.service_line_item?.sub_total ??
        (Number(row?.quantity || 0) * Number(row?.service_line_item?.rate || 0))
    );

    const utilizationColumns = [
        {
            name: "Consumption Date",
            selector: (row) => formatDate(row?.created_at),
            sortable: false,
            minWidth: "160px",
        },
        {
            name: "Requester",
            selector: (row) => getRequesterFromRow(row).username,
            cell: (row) => {
                const requester = getRequesterFromRow(row);
                return (
                    <span className="part_utilization_requester">
                        <UserAvatar
                            username={requester.username}
                            image={requester.image}
                            size={24}
                            fontSize="11px"
                            marginRight="0"
                        />
                        <span className="hyperlink">{requester.username}</span>
                    </span>
                );
            },
            sortable: false,
            minWidth: "220px",
        },
        {
            name: "Work Order No.",
            selector: (row) => getUtilizationWorkOrderLabel(row),
            cell: (row) => {
                const label = getUtilizationWorkOrderLabel(row);
                const workOrderRouteId = getUtilizationWorkOrderRouteId(row);

                if (!workOrderRouteId) return <span>{label}</span>;

                return (
                    <span
                        className="hyperlink"
                        onClick={() => navigate(`/work-orders/work-order-details/${workOrderRouteId}`)}
                        style={{ cursor: "pointer" }}
                    >
                        {label}
                    </span>
                );
            },
            sortable: false,
            minWidth: "160px",
        },
        // {
        //     name: "Asset",
        //     selector: (row) => getAssetLabel(row),
        //     sortable: false,
        //     minWidth: "160px",
        //     wrap: true,
        // },
        {
            name: "Quantity Removed",
            selector: (row) => Number(row?.quantity || row?.service_line_item?.qty || 0),
            sortable: false,
            minWidth: "150px",
        },
        {
            name: "Landed Cost (PKR)",
            selector: (row) => formatAmount(getUtilizationLandedCost(row)),
            sortable: false,
            minWidth: "170px",
        },
    ];

    const partTabs = [
        {
            key: "detail",
            label: "Details & Specifications",
            icon: activeTab === "detail" ? activeDetailsIcon : detailsIcon,
        },
        {
            key: "procurement",
            label: "Procurement",
            icon: activeTab === "procurement" ? activeHistoryIcon : historyIcon,
        },
        {
            key: "utilization",
            label: "Utilization",
            icon: activeTab === "utilization" ? activeViolationsIcon : violationsIcon,
        }
    ];

    if (getPartDetailLoading) {
        return <SpinnerLoader />;
    }

    const rawStockCount = Number(getPartDetailData?.stock_count ?? 0);
    const rawStockThreshold = Number(getPartDetailData?.stock_threshold ?? 0);
    const stockCount = Number.isFinite(rawStockCount) ? rawStockCount : 0;
    const stockThreshold = Number.isFinite(rawStockThreshold) ? rawStockThreshold : 0;
    const stockIndicatorClass =
        stockThreshold <= 0 || stockCount <= 0
            ? "available_stock_count--red"
            : stockCount < stockThreshold
                ? "available_stock_count--yellow"
                : "available_stock_count--green";

    return (
        <>
            <Breadcrumbs values={["Inventory", "Part Details"]} />

            <Formik
                initialValues={initialValues}
                validationSchema={partFullSchema}
                enableReinitialize={true}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        const mapFileNames = (files = []) =>
                            files
                                .map((file) => file?.fileName || file?.file_path || file?.path || file)
                                .filter(Boolean);

                        const currentFiles = mapFileNames(uploadedFiles);
                        const initialFiles = initialUploadedFileNames;

                        const getPayload = (payloadValues, payloadFiles) => ({
                            part_id: id,
                            name: payloadValues.part_name,
                            company_name: payloadValues.company_name,
                            part_number: payloadValues.part_number,
                            sku: payloadValues.sku,
                            maintenance_category_id: payloadValues.category,
                            description: payloadValues.description,
                            length: payloadValues.length,
                            width: payloadValues.width,
                            height: payloadValues.height,
                            weight: payloadValues.weight,
                            material: payloadValues.material,
                            model: payloadValues.model,
                            make: payloadValues.make,
                            year: payloadValues.year,
                            engine_code: payloadValues.engineCode,
                            cross_reference_sku: payloadValues.crossReferenceSku,
                            max_pressure: payloadValues.maxPressure,
                            max_voltage: payloadValues.maxVoltage,
                            max_temperature: payloadValues.maxTemperature,
                            unit_id: payloadValues.unitOfMeasurement,
                            stock_threshold: payloadValues.safetyStockThreshold,
                            stock_count: payloadValues.availableStock,
                            price: payloadValues.price,
                            note: payloadValues.notes,
                            files: payloadFiles
                        });

                        const currentPayload = getPayload(values, currentFiles);
                        const initialPayload = getPayload(initialValues, initialFiles);

                        let changedKeys = Object.keys(currentPayload).filter((key) => {
                            return !_.isEqual(currentPayload[key], initialPayload[key]);
                        });

                        const payload = {
                            ...currentPayload,
                            updatedKeys: changedKeys,
                        };

                        await dispatch(partUpdate({ data: payload })).unwrap();
                        successNotify("Part updated successfully.");
                        dispatch(getPartDetail(id));
                    } catch (error) {
                        errorNotify(error);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({
                    values, errors, touched, handleChange, handleBlur, setFieldValue, handleSubmit
                }) => (
                    <>
                        <div className="page_header">
                            <h2>Part Details</h2>

                            <div className="d-flex align-items-center gap-2">
                                <PermissionWrapper
                                    module={InventorySubModules.Parts}
                                    permission={PartsPermission.Update}
                                    mainModule={AppMainModules.inventoryManagement}
                                >
                                    <Button
                                        cancelBtn={() => navigate(-1)}
                                        loading={partUpdateLoading}
                                        disabled={partUpdateLoading}
                                        submitText={partUpdateLoading ? "Saving..." : "Save Details"}
                                        type="button"
                                        shouldRow={false}
                                        isShowSubmitBtn={true}
                                        submitBtn={handleSubmit}
                                    />
                                </PermissionWrapper>
                            </div>
                        </div>
                        <div className="page_container">
                            <Row>
                                <Col md={8}>
                                    <div className="trip_details_main_div">
                                        <div>
                                            <h6>
                                                Part ID: <span>{getPartDetailData?.part_id || "N/A"}</span>
                                            </h6>
                                        </div>

                                        <div className={`available_stock_count ${stockIndicatorClass}`}>
                                            <h5>Available Stock Count</h5>
                                            <div className="available_stock_count_value">
                                                {stockCount.toLocaleString()}
                                            </div>
                                        </div>

                                        <CustomTabs
                                            tabs={partTabs}
                                            activeTab={activeTab}
                                            setActiveTab={setActiveTab}
                                        />
                                    </div>

                                    {activeTab === "detail" && (
                                        <>
                                            <div className="content_box">
                                                <h6>General Details</h6>
                                                <p>Main information for this part record.</p>

                                                <Row>
                                                    <Col md={12}>
                                                        <Input
                                                            label="Description"
                                                            placeholder="Enter part description (e.g., specifications, usage, dimensions, or any relevant details)"
                                                            type="text"
                                                            name="description"
                                                            className="part_description_editor"
                                                            value={values.description}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            error={errors.description}
                                                            touched={touched.description}
                                                            quill={true}
                                                            setFieldValue={setFieldValue}
                                                        />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input label="Part Name" placeholder="Engine Oil" name="part_name" value={values.part_name} onChange={handleChange} onBlur={handleBlur} error={errors.part_name} touched={touched.part_name} />
                                                    </Col>

                                                    <Col md={6}>
                                                        <Input label="Part Number" placeholder="PN-12345" name="part_number" value={values.part_number} onChange={handleChange} onBlur={handleBlur} error={errors.part_number} touched={touched.part_number} />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input label="SKU" placeholder="SKU-67890" name="sku" value={values.sku} onChange={handleChange} onBlur={handleBlur} error={errors.sku} touched={touched.sku} />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Select label="Category" placeholder="Select" options={partCategoriesDropdownData} value={partCategoriesDropdownData?.find(o => o.value === values.category)} loading={dropdownLoading} name="category" onChange={(opt) => setFieldValue('category', opt ? opt.value : '')} onBlur={handleBlur} error={errors.category} touched={touched.category} />
                                                    </Col>
                                                    <Col md={6}>
                                                        <Input label="Company Name" placeholder="Enter Company Name" name="company_name" value={values.company_name} onChange={handleChange} onBlur={handleBlur} error={errors.company_name} touched={touched.company_name} />
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div className="content_box">
                                                <h6>Specifications</h6>
                                                <p>Main information for this part record.</p>
                                                <Row>
                                                    <Col md={4}>
                                                        <Select label="Material" placeholder="Select" options={partsMaterialDropdownData} value={partsMaterialDropdownData?.find(o => o.value === values.material)} loading={dropdownLoading} name="material" onChange={(opt) => setFieldValue('material', opt ? opt.value : '')} onBlur={handleBlur} error={errors.material} touched={touched.material} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Select label="Unit of Measurement" options={partsUnitsDropdownData} value={partsUnitsDropdownData?.find(o => o.value === values.unitOfMeasurement)} loading={dropdownLoading} name="unitOfMeasurement" onChange={(opt) => setFieldValue('unitOfMeasurement', opt ? opt.value : '')} onBlur={handleBlur} error={errors.unitOfMeasurement} touched={touched.unitOfMeasurement} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Length (cm)" type="number" name="length" value={values.length} onChange={handleChange} onBlur={handleBlur} error={errors.length} touched={touched.length} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Height (cm)" type="number" name="height" value={values.height} onChange={handleChange} onBlur={handleBlur} error={errors.height} touched={touched.height} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Width (cm)" type="number" name="width" value={values.width} onChange={handleChange} onBlur={handleBlur} error={errors.width} touched={touched.width} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Weight (kg)" type="number" name="weight" value={values.weight} onChange={handleChange} onBlur={handleBlur} error={errors.weight} touched={touched.weight} />
                                                    </Col>

                                                    <Col md={12} className="px-0">
                                                        <h5>Compatibility</h5>
                                                    </Col>

                                                    <Col md={4}>
                                                        <Select label="Make" placeholder="Select" options={partsMakesDropdownData} value={partsMakesDropdownData?.find(o => o.value === values.make)} loading={dropdownLoading} name="make" onChange={(opt) => setFieldValue('make', opt ? opt.value : '')} onBlur={handleBlur} error={errors.make} touched={touched.make} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Select label="Model" placeholder="Select" options={partsModelDropdownData} value={partsModelDropdownData?.find(o => o.value === values.model)} loading={dropdownLoading} name="model" onChange={(opt) => setFieldValue('model', opt ? opt.value : '')} onBlur={handleBlur} error={errors.model} touched={touched.model} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Year" type="number" name="year" value={values.year} onChange={handleChange} onBlur={handleBlur} error={errors.year} touched={touched.year} />
                                                    </Col>

                                                    <Col md={4}>
                                                        <Input label="Engine Code" name="engineCode" value={values.engineCode} onChange={handleChange} onBlur={handleBlur} error={errors.engineCode} touched={touched.engineCode} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Cross Reference SKU" name="crossReferenceSku" value={values.crossReferenceSku} onChange={handleChange} onBlur={handleBlur} error={errors.crossReferenceSku} touched={touched.crossReferenceSku} />
                                                    </Col>

                                                    <Col md={12} className="px-0">
                                                        <h5>Constraints</h5>
                                                    </Col>


                                                    <Col md={4}>
                                                        <Input label="Max Pressure" name="maxPressure" value={values.maxPressure} onChange={handleChange} onBlur={handleBlur} error={errors.maxPressure} touched={touched.maxPressure} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Max Voltage" name="maxVoltage" value={values.maxVoltage} onChange={handleChange} onBlur={handleBlur} error={errors.maxVoltage} touched={touched.maxVoltage} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Max Temperature" name="maxTemperature" value={values.maxTemperature} onChange={handleChange} onBlur={handleBlur} error={errors.maxTemperature} touched={touched.maxTemperature} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Safety Stock Threshold" type="number" name="safetyStockThreshold" value={values.safetyStockThreshold} onChange={handleChange} onBlur={handleBlur} error={errors.safetyStockThreshold} touched={touched.safetyStockThreshold} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Available Stock" type="number" name="availableStock" value={values.availableStock} onChange={handleChange} onBlur={handleBlur} error={errors.availableStock} touched={touched.availableStock} />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Input label="Unit Price" type="number" step="0.01" name="price" value={values.price} onChange={handleChange} onBlur={handleBlur} error={errors.price} touched={touched.price} />
                                                    </Col>
                                                </Row>
                                            </div>

                                            <div className="content_box">
                                                <h6>Attachment</h6>
                                                <p>Upload a single image for this part.</p>
                                                <div className="issue_file_upload_wrapper">
                                                    <FileUpload
                                                        destination="service-entry"
                                                        files={uploadedFiles}
                                                        setFiles={setUploadedFiles}
                                                        maxFileSize={10}
                                                        maxFiles={1}
                                                        accept="image/*"
                                                        className="issue_file_upload"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {activeTab === "procurement" && (
                                        <div className="content_box">
                                            <h6>Procurement Information</h6>
                                            <p>A non-editable ledger tracking the sourcing history and financial acquisition of the part.</p>
                                            <NewTableComponent
                                                showFilter={false}
                                                columns={procurementColumns}
                                                data={procurementRows}
                                                currentPage={procurementMeta.currentPage || procurementCurrentPage}
                                                setCurrentPage={setProcurementCurrentPage}
                                                limit={procurementLimit}
                                                setLimit={(val) => {
                                                    setProcurementCurrentPage(1);
                                                    setProcurementLimit(val);
                                                }}
                                                totalItems={procurementMeta.totalItems || 0}
                                                totalPages={procurementMeta.totalPages || 1}
                                                loading={getPartPaginatedForPOloading}
                                                paginationLoading={getPartPaginatedForPOloading}
                                                showPagination={true}
                                                limitOptions={[10, 20, 50, 100]}
                                                paginationClassName="inline_pagination"
                                            />
                                        </div>
                                    )}

                                    {activeTab === "utilization" && (
                                        <div className="content_box">
                                            <h6>Utilization Information</h6>
                                            <p>An audit trail tracking the consumption of the part to ensure accountability.</p>
                                            <NewTableComponent
                                                showFilter={false}
                                                columns={utilizationColumns}
                                                data={utilizationRows}
                                                currentPage={utilizationMeta.currentPage || utilizationCurrentPage}
                                                setCurrentPage={setUtilizationCurrentPage}
                                                limit={utilizationLimit}
                                                setLimit={(val) => {
                                                    setUtilizationCurrentPage(1);
                                                    setUtilizationLimit(val);
                                                }}
                                                totalItems={utilizationMeta.totalItems || 0}
                                                totalPages={utilizationMeta.totalPages || 1}
                                                loading={getPartUtilizationLoading}
                                                paginationLoading={getPartUtilizationLoading}
                                                showPagination={true}
                                                limitOptions={[10, 20, 50, 100]}
                                                paginationClassName="inline_pagination"
                                            />
                                        </div>
                                    )}
                                </Col>

                                <Col md={4}>
                                    <div>
                                        <PartHistory />
                                    </div>
                                    <div className='trip_notes_main_div' style={{ marginTop: "15px", minHeight: "fit-content", height: "auto" }}>
                                        <h5>Notes</h5>

                                        <ReactQuill
                                            placeholder={'Write notes here...'}
                                            modules={modules}
                                            theme="snow"
                                            value={values.notes || ''}
                                            formats={formats}
                                            onChange={(content) => setFieldValue('notes', content)}
                                            className='react_quill_input notes_quill_input quill-small-font trip_quill_input'
                                        />
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </>
                )}
            </Formik>
        </>
    );
};

export default PartDetails;
